import { useEffect, useState } from 'react';
import { parse } from 'src/_shared/services/utils';
import { gql } from 'graphql-tag';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
	BADGE_TYPE,
} from '../../../RnRCommonComponent/CoreBadge/rewardsConstants.js';

const useBadgeList = (
	badgeType,
	shouldRefresh,
	setShouldRefresh,
	getMyBadge = false,
	currentUser,
	client
) => {
	const [isLoading, setIsLoading] = useState(false);
	const [badgeList, setBadgeList] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [nextToken, setNextToken] = useState(null);

	const QUERY_BADGES_BY_COMPANY_ID = gql`
		query QueryBadgesByCompanyIdIndex(
			$companyId: ID!
			$limit: Int
			$nextToken: String
		) {
			queryBadgesByCompanyIdIndex(
				companyId: $companyId
				limit: $limit
				nextToken: $nextToken
			) {
				items {
					id
					companyId
					badgeTypeId
					badgeLimitPerUser {
						limit
						frequency
					}
					assignmentSSORequiredId
					recipientPayTypeId
					recipientSSORequiredId
					physicalAwardActivityId
					iconId
					snappyGrantId
					snappyGrant {
						id
						snappyCampaignId
						name
						description
						costInCurrency
					}
					name
					label
					description
					image
					color
					gradientColor
					points
					bonusPoints
					rewardBonus
					rewardMaxBonus
					assignmentLimit
					assignmentLevelId
					recipientLevelId
					isPreApproved
					isRewardRequiresDate
					visibility
					isAssignmentSSORequired
					isRecipientSSORequired
					automationRule
					externalWorkFlowId
					isAssignmentRestricted
					shape
					badgeRotation
					customImageKey
					customImageSignedUrl
					labelColor
					customIconKey
					customIconSignedUrl
					category
				}
				nextToken
			}
		}
	`;

	const fetchBadgesByCompany = async (
		forceRefresh = false,
		tokenToUse = null
	) => {
		if (client && currentUser?.companyId) {
			setIsLoading(true);
			try {
				if (forceRefresh) {
					localStorage.removeItem(`badgeList-${badgeType}`);
					setNextToken(null);
				}

				const { data, error } = await client.query({
					query: QUERY_BADGES_BY_COMPANY_ID,
					variables: {
						companyId: currentUser.companyId,
						limit: 100,
						nextToken: tokenToUse,
					},
					fetchPolicy: 'network-only',
				});
				if (error) {
					console.error('Error fetching badges by company ID:', error);
					return;
				}

				if (data?.queryBadgesByCompanyIdIndex?.items) {
					const enrichedBadges = data.queryBadgesByCompanyIdIndex.items.map(
						(badge) => {
							const badgeTypeCode =
								REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId] || '';

							let iconInfo = null;
							if (badge.iconId) {
								iconInfo = ICON_COMPLETE_MAP.find(
									(icon) => icon.id === badge.iconId
								);
							}

							return {
								...badge,
								badgeType: BADGE_TYPE[badgeTypeCode],
								iconInfo,
							};
						}
					);

					const filteredBadges = enrichedBadges.filter(
						(badge) => badge.badgeType === badgeType
					);
					const newNextToken = data.queryBadgesByCompanyIdIndex.nextToken;

					setNextToken(newNextToken);
					setHasMore(!!newNextToken);

					return {
						code: 200,
						badges: filteredBadges,
						nextToken: newNextToken,
					};
				}
			} catch (error) {
				console.error('Exception when fetching badges by company ID:', error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	const getAllBadgeList = async (refresh = false, isLoadingMore = false) => {
		try {
			setIsLoading(true);
			const tokenToUse = isLoadingMore ? nextToken : null;
			let response = await fetchBadgesByCompany(refresh, tokenToUse);

			if (response?.code === 200) {
				setShouldRefresh(false);

				if (isLoadingMore) {
					setBadgeList((prevList) => {
						const combined = [...prevList, ...response.badges];
						const dedupedCache = combined.filter(
							(badge, index, self) =>
								index === self.findIndex((b) => b.id === badge.id)
						);
						localStorage.setItem(
							`badgeList-${badgeType}`,
							JSON.stringify(dedupedCache)
						);
						return dedupedCache;
					});
				} else {
					setBadgeList(response.badges);
					const dedupedCache = response.badges.filter(
						(badge, index, self) =>
							index === self.findIndex((b) => b.id === badge.id)
					);
					localStorage.setItem(
						`badgeList-${badgeType}`,
						JSON.stringify(dedupedCache)
					);
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (shouldRefresh) {
			getAllBadgeList(true, false);
		}
	}, [shouldRefresh]);

	useEffect(() => {
		if (getMyBadge) {
		} else {
			getAllBadgeList(false, false);
		}
	}, [getMyBadge, badgeType]);

	const fetchMoreData = () => {
		if (hasMore && !isLoading) {
			getAllBadgeList(false, true);
		}
	};

	const cachedBadgeList = parse(
		localStorage.getItem(`badgeList-${getMyBadge ? 'myBadges' : badgeType}`)
	);
	return {
		isLoading,
		badgeList: badgeList.length > 0 ? badgeList : cachedBadgeList || [],
		hasMore,
		fetchMoreData,
	};
};

export default useBadgeList;
